
import React from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { AssetClassification } from "@/types/classifications";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { AssetClassificationEditor } from "./AssetClassificationEditor";
import { logger } from "@/services/LoggingService";
import { Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ClassificationListProps {
  classifications: AssetClassification[];
  onSelect: (classification: AssetClassification) => void;
  onClone: (classification: AssetClassification) => void;
  onDelete: (id: string) => void;
  onEdit: (classification: AssetClassification) => void;
  onAdd?: () => void;
  selectedId?: string;
}

const getStatusColor = (status: AssetClassification['status']) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'draft':
      return 'secondary';
    case 'deprecated':
      return 'destructive';
    default:
      return 'default';
  }
};

// Helper function to count nodes in a classification tree
const countNodes = (classification: AssetClassification['classification']): number => {
  if (!classification) return 0;
  return 1 + (classification.children?.reduce((acc, child) => acc + countNodes(child), 0) || 0);
};

export function ClassificationList({ 
  classifications, 
  onSelect, 
  onClone, 
  onDelete,
  onEdit,
  onAdd,
  selectedId 
}: ClassificationListProps) {
  const [editingClassification, setEditingClassification] = React.useState<AssetClassification | null>(null);

  const handleEdit = (classification: AssetClassification, e: React.MouseEvent) => {
    e.stopPropagation();
    logger.debug('classifications.list', 'Opening edit modal', {
      classificationId: classification.id,
      timestamp: new Date().toISOString()
    });
    setEditingClassification(classification);
  };

  const handleUpdate = (classification: AssetClassification) => {
    logger.debug('classifications.list', 'Saving classification edits', {
      classificationId: classification.id,
      timestamp: new Date().toISOString()
    });
    onEdit(classification);
    setEditingClassification(null);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Asset Classifications</h3>
        {onAdd && (
          <Button onClick={onAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add Classification
          </Button>
        )}
      </div>

      <ScrollArea className="h-[300px]">
        <div className="p-4 space-y-4">
          {classifications.map((classification) => {
            const nodeCount = countNodes(classification.classification);
            return (
              <Card
                key={classification.id}
                onClick={() => onSelect(classification)}
                className={cn(
                  "p-4 transition-colors cursor-pointer border",
                  "hover:bg-accent/50",
                  selectedId === classification.id && "border-primary"
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-lg font-medium">
                        {classification.display_name}
                      </h4>
                      <Badge variant={getStatusColor(classification.status)} className="capitalize">
                        {classification.status}
                      </Badge>
                      <Badge variant="outline" className="ml-2">
                        {nodeCount} nodes
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {classification.reference_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sheet 
                      open={editingClassification?.id === classification.id}
                      onOpenChange={(open) => {
                        if (!open) setEditingClassification(null);
                      }}
                    >
                      <SheetTrigger asChild>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => handleEdit(classification, e)}
                        >
                          Edit
                        </Button>
                      </SheetTrigger>
                      <SheetContent>
                        <SheetHeader>
                          <SheetTitle>Edit Asset Classification</SheetTitle>
                          <SheetDescription>
                            Make changes to your asset classification details
                          </SheetDescription>
                        </SheetHeader>
                        {editingClassification && (
                          <AssetClassificationEditor
                            classification={editingClassification}
                            onSave={handleUpdate}
                            onCancel={() => setEditingClassification(null)}
                          />
                        )}
                      </SheetContent>
                    </Sheet>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClone(classification);
                      }}
                    >
                      Clone
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(classification.id);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </ScrollArea>
    </>
  );
}
